import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../Dashboard/Sidebar";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Paper,
  Stack,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowBack,
} from "@mui/icons-material";
import { calculateSeoScore } from "../../utils/seo/calculateSeoScore";
import SeoScorePanel from "../../Components/blog/SeoScorePanel";
import SeoChecklist from "../../Components/blog/SeoChecklist";
import RichTextEditor from "../../Components/blog/RichTextEditor";
import { blogService } from "../../services/blogService";
import DOMPurify from "dompurify";

// --- PREMIUM LIGHT THEME (matching AddCar.js) ---
const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#F8F9FB", paper: "#FFFFFF" },
    primary: { main: "#111827" },
    secondary: { main: "#6366F1" },
    text: { primary: "#111827", secondary: "#6B7280" },
    divider: "#F3F4F6",
    error: { main: "#EF4444" },
    success: { main: "#10B981" },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            fontSize: "0.925rem",
            transition: "all 0.2s ease",
            "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1.5px" },
            "&:hover fieldset": { borderColor: "#D1D5DB" },
            "&.Mui-focused": {
              backgroundColor: "#FFFFFF",
              boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
              "& fieldset": { borderColor: "#6366F1", borderWidth: "1.5px" },
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#6B7280",
            "&.Mui-focused": { color: "#6366F1" },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none" } },
    },
  },
});

// --- STYLED COMPONENTS ---
const RootContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#F8F9FB",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  width: "calc(100% - 18vw)",
  padding: "40px",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const SectionCard = styled(Paper)({
  padding: "32px",
  borderRadius: "16px",
  border: "1px solid #F3F4F6",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
  marginBottom: "24px",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow:
      "0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03)",
  },
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "1.05rem",
  color: "#111827",
  letterSpacing: "-0.01em",
  lineHeight: 1.4,
});

const SectionSubtitle = styled(Typography)({
  fontSize: "0.825rem",
  color: "#9CA3AF",
  marginTop: "4px",
  fontWeight: 400,
  lineHeight: 1.5,
});

const PublishButton = styled(Button)({
  padding: "14px 40px",
  fontSize: "0.95rem",
  fontWeight: 700,
  borderRadius: "12px",
  backgroundColor: "#111827",
  color: "#FFFFFF",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  letterSpacing: "-0.01em",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#1F2937",
    boxShadow: "0 4px 14px rgba(17, 24, 39, 0.2)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
});

// --- OPTION DATA ---
const blogCategories = [
  "Buying Guide",
  "Comparison",
  "Tips & Tricks",
  "News",
  "Reviews",
  "Industry Insights",
  "How-To",
  "Opinion",
];

// --- COMPONENT ---
function BlogEditor() {
  const navigate = useNavigate();
  const { slug: editSlug } = useParams();
  const isEditMode = Boolean(editSlug);

  const AUTHOR_NAME = "FLYWHEEL";

  const defaultFormData = {
    id: "",
    title: "",
    slug: "",
    featuredImage: "",
    author: AUTHOR_NAME,
    category: "",
    description: "",
    content: "",
    metaTitle: "",
    metaDescription: "",
    focusKeyword: "",
    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    createdAt: "",
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editorMode, setEditorMode] = useState("visual");

  // Fetch blog data in edit mode using slug
  useEffect(() => {
    const fetchBlog = async () => {
      if (!isEditMode) return;
      
      try {
        const response = await blogService.getBlogBySlug(editSlug);
        if (response.success && response.data) {
          const blogData = response.data;
          setFormData({
            ...blogData,
            author: AUTHOR_NAME,
          });
        }
      } catch (error) {
        toast.error("Failed to fetch blog details");
        navigate("/blogs");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlog();
  }, [editSlug, isEditMode, navigate]);

  const slugify = useCallback((value) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
  }, []);

  // Auto-generate slug from title (only in create mode)
  useEffect(() => {
    if (isEditMode) return;
    const slug = slugify(formData.title || "");
    setFormData((prev) => (prev.slug !== slug ? { ...prev, slug } : prev));
  }, [formData.title, isEditMode, slugify]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "slug") {
        return { ...prev, slug: slugify(value) };
      }
      if (name === "author") {
        // Author is fixed for now
        return { ...prev, author: AUTHOR_NAME };
      }
      return { ...prev, [name]: value };
    });
    setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
  }, [slugify]);

  const countWords = useCallback((html) => {
    if (!html) return 0;
    const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!text) return 0;
    return text.split(" ").filter(Boolean).length;
  }, []);

  // Debounced SEO input state to avoid heavy recalculations
  const [seoInput, setSeoInput] = useState(defaultFormData);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSeoInput(formData);
    }, 400);
    return () => clearTimeout(timer);
  }, [formData]);

  // Live SEO score based on debounced input
  const seoResult = useMemo(
    () => calculateSeoScore(seoInput),
    [seoInput]
  );

  const validateForm = useCallback(() => {
    const errors = {};
    const trimmedTitle = formData.title.trim();
    const trimmedSlug = formData.slug.trim();
    const trimmedMetaTitle = formData.metaTitle.trim();
    const trimmedMetaDescription = formData.metaDescription.trim();
    const trimmedFeaturedImage = formData.featuredImage.trim();
    const contentWordCount = countWords(formData.content);

    if (!trimmedTitle) {
      errors.title = "Title is required.";
    } else if (trimmedTitle.length < 20) {
      errors.title = "Title must be at least 20 characters.";
    }

    if (!trimmedSlug) {
      errors.slug = "Slug is required.";
    } else if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
      errors.slug = "Slug must be URL friendly (lowercase letters, numbers, and hyphens only).";
    }

    if (!formData.content || formData.content === "<p></p>") {
      errors.content = "Content is required.";
    } else if (contentWordCount < 300) {
      errors.content = "Content must be at least 300 words.";
    }

    if (!trimmedMetaTitle) {
      errors.metaTitle = "Meta title is required.";
    } else if (trimmedMetaTitle.length < 50 || trimmedMetaTitle.length > 60) {
      errors.metaTitle = "Meta title should be between 50 and 60 characters.";
    }

    if (!trimmedMetaDescription) {
      errors.metaDescription = "Meta description is required.";
    } else if (trimmedMetaDescription.length < 140 || trimmedMetaDescription.length > 160) {
      errors.metaDescription = "Meta description should be between 140 and 160 characters.";
    }

    if (!trimmedFeaturedImage) {
      errors.featuredImage = "Featured image URL is required.";
    }

    if (Object.keys(errors).length) {
      Object.values(errors).forEach((msg) => toast.error(msg));
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [countWords, formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    const sanitizedContent = DOMPurify.sanitize(formData.content || "", {
      USE_PROFILES: { html: true },
    });

    setIsSaving(true);

    try {
      // Recalculate SEO score at submit time using latest content
      const seoForSave = calculateSeoScore({
        ...formData,
        content: sanitizedContent,
      });

      const payload = {
        title: formData.title,
        slug: formData.slug,
        author: AUTHOR_NAME,
        category: formData.category,
        description: formData.description,
        content: sanitizedContent,
        featuredImage: formData.featuredImage,
        focusKeyword: formData.focusKeyword,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        canonicalUrl: formData.canonicalUrl,
        ogTitle: formData.ogTitle,
        ogDescription: formData.ogDescription,
        ogImage: formData.ogImage,
        seoScore: seoForSave.score,
      };

      let response;
      if (isEditMode) {
        response = await blogService.updateBlog(formData.id, payload);
      } else {
        response = await blogService.createBlog(payload);
      }

      if (response?.success) {
        toast.success(isEditMode ? "Blog updated successfully!" : "Blog created successfully!");
        setTimeout(() => {
          navigate("/blogs", { state: { blogData: { ...payload, id: response.data?.id || formData.id } } });
        }, 600);
      } else {
        toast.error(
          response?.message ||
            (isEditMode ? "Failed to update blog." : "Failed to create blog.")
        );
      }
    } catch (err) {
      toast.error(isEditMode ? "Failed to update blog." : "Failed to create blog.");
    } finally {
      setIsSaving(false);
    }
  }, [AUTHOR_NAME, formData, isEditMode, navigate, validateForm]);

  if (isLoading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <RootContainer>
          <Sidebar />
          <ContentArea sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <CircularProgress />
          </ContentArea>
        </RootContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <RootContainer>
        <Sidebar />
        <ContentArea>
          {/* ── HEADER ── */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{ mb: 5 }}
          >
            <Box>
              <Button
                onClick={() => navigate("/blogs")}
                startIcon={
                  <ArrowBack
                    sx={{
                      fontSize: "1rem",
                      transition: "transform 0.2s ease",
                    }}
                  />
                }
                sx={{
                  mb: 1.5,
                  px: 1.5,
                  py: 0.5,
                  color: "#6B7280",
                  fontSize: "0.825rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  letterSpacing: "-0.01em",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                    color: "#111827",
                    "& .MuiButton-startIcon svg": {
                      transform: "translateX(-2px)",
                    },
                  },
                }}
              >
                Go Back
              </Button>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#111827",
                  letterSpacing: "-0.025em",
                  fontSize: { xs: "1.75rem", md: "2rem" },
                }}
              >
                {isEditMode ? "Edit Blog" : "Create Blog"}
              </Typography>
              <Typography
                sx={{ color: "#9CA3AF", fontSize: "0.875rem", mt: 0.5 }}
              >
                {isEditMode
                  ? "Update your blog post details"
                  : "Fill in the details to publish a new blog post"}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={3}>
            {/* ── LEFT: FORM ── */}
            <Grid item xs={12} lg={8}>
              {/* SECTION 1: Blog Details */}
              <SectionCard>
                <Box sx={{ mb: 3 }}>
                  <SectionTitle>Blog Details</SectionTitle>
                  <SectionSubtitle>
                    Basic information about the blog post
                  </SectionSubtitle>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      name="title"
                      placeholder="Enter blog title"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Slug"
                      name="slug"
                      placeholder="auto-generated-slug"
                      value={formData.slug}
                      onChange={handleChange}
                      disabled={!isEditMode}
                      error={Boolean(validationErrors.slug)}
                      helperText={validationErrors.slug}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Author"
                      name="author"
                      value={AUTHOR_NAME}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Featured Image URL"
                      name="featuredImage"
                      placeholder="https://example.com/image.jpg"
                      value={formData.featuredImage}
                      onChange={handleChange}
                      error={Boolean(validationErrors.featuredImage)}
                      helperText={validationErrors.featuredImage}
                    />
                  </Grid>
                  {/* Image Preview */}
                  {formData.featuredImage && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          borderRadius: "12px",
                          overflow: "hidden",
                          border: "1px solid #E5E7EB",
                          height: 200,
                          backgroundImage: `url(${formData.featuredImage})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >
                      {blogCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </SectionCard>

              {/* SECTION 2: Content */}
              <SectionCard>
                <Box sx={{ mb: 3 }}>
                  <SectionTitle>Content</SectionTitle>
                  <SectionSubtitle>
                    Write the blog description and full content
                  </SectionSubtitle>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Short Description"
                      name="description"
                      placeholder="A brief excerpt for the blog listing"
                      multiline
                      rows={2}
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                     <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151", mb: 1.5, ml: 0.5 }}>
                      Blog Content
                    </Typography>
                    <Box sx={{ mb: 1.5 }}>
                      <ToggleButtonGroup
                        value={editorMode}
                        exclusive
                        size="small"
                        onChange={(_, mode) => {
                          if (mode) setEditorMode(mode);
                        }}
                        sx={{
                          backgroundColor: "#F3F4F6",
                          borderRadius: "999px",
                          p: 0.25,
                        }}
                      >
                        <ToggleButton
                          value="visual"
                          sx={{
                            px: 2,
                            textTransform: "none",
                            fontSize: "0.8rem",
                            "&.Mui-selected": {
                              backgroundColor: "#FFFFFF",
                              color: "#111827",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          Visual Editor
                        </ToggleButton>
                        <ToggleButton
                          value="html"
                          sx={{
                            px: 2,
                            textTransform: "none",
                            fontSize: "0.8rem",
                            "&.Mui-selected": {
                              backgroundColor: "#FFFFFF",
                              color: "#111827",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          HTML Source
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {editorMode === "visual" ? (
                      <RichTextEditor
                        value={formData.content}
                        onChange={(html) =>
                          handleChange({ target: { name: "content", value: html } })
                        }
                      />
                    ) : (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={10}
                          maxRows={24}
                          name="content"
                          value={formData.content}
                          onChange={(e) =>
                            handleChange({
                              target: { name: "content", value: e.target.value },
                            })
                          }
                          placeholder="<h2>The Ultimate Luxury Cars to Watch in 2025</h2>\n<p>The luxury car market in 2025 is more exciting than ever...</p>"
                          sx={{
                            fontFamily: "monospace",
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#0F172A",
                              color: "#E5E7EB",
                              fontSize: "0.875rem",
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: { xs: 0.5, sm: 0.5 },
                            py: 1,
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", fontWeight: 500 }}
                          >
                            Word count: {countWords(formData.content)} words
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Reading time:{" "}
                            {Math.max(
                              1,
                              Math.ceil(countWords(formData.content) / 200)
                            )}{" "}
                            min
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </SectionCard>

              {/* SECTION 3: SEO Settings */}
              <SectionCard>
                <Box sx={{ mb: 3 }}>
                  <SectionTitle>SEO Settings</SectionTitle>
                  <SectionSubtitle>
                    Optimize your blog post for search engines
                  </SectionSubtitle>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Meta Title"
                      name="metaTitle"
                      placeholder="SEO-optimized page title (50–60 chars)"
                      value={formData.metaTitle}
                      onChange={handleChange}
                      helperText={`${formData.metaTitle.length}/60 characters`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Meta Description"
                      name="metaDescription"
                      placeholder="Compelling description for search results (140–160 chars)"
                      multiline
                      rows={3}
                      value={formData.metaDescription}
                      onChange={handleChange}
                      helperText={`${formData.metaDescription.length}/160 characters`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Focus Keyword"
                      name="focusKeyword"
                      placeholder="e.g. luxury cars"
                      value={formData.focusKeyword}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* SECTION 4: Technical SEO */}
              <SectionCard>
                <Box sx={{ mb: 3 }}>
                  <SectionTitle>Technical SEO</SectionTitle>
                  <SectionSubtitle>
                    Advanced metadata for social sharing and search engines
                  </SectionSubtitle>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                     <TextField
                      fullWidth
                      label="Canonical URL"
                      name="canonicalUrl"
                      placeholder="https://example.com/blog/my-post"
                      value={formData.canonicalUrl}
                      onChange={handleChange}
                      helperText="Optional. Use to prevent duplicate content issues."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="OpenGraph Title"
                      name="ogTitle"
                      placeholder="Custom title for Facebook/LinkedIn sharing"
                      value={formData.ogTitle}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="OpenGraph Description"
                      name="ogDescription"
                      placeholder="Custom description for social sharing"
                      multiline
                      rows={2}
                      value={formData.ogDescription}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="OpenGraph Image URL"
                      name="ogImage"
                      placeholder="https://example.com/og-image.jpg"
                      value={formData.ogImage}
                      onChange={handleChange}
                      helperText="Recommended size: 1200x630 pixels."
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* PUBLISH BUTTON */}
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{ mt: 1, mb: 4 }}
              >
                <PublishButton onClick={handleSubmit} disabled={isSaving}>
                  {isSaving ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? "Update Blog" : "Publish Blog")}
                </PublishButton>
              </Stack>
            </Grid>

            {/* ── RIGHT: SEO SCORE PANEL ── */}
            <Grid item xs={12} lg={4}>
              <SectionCard
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: "40px" },
                  padding: "24px",
                }}
              >
                <SeoScorePanel score={seoResult.score} />
                <SeoChecklist breakdown={seoResult.breakdown} />
              </SectionCard>
            </Grid>
          </Grid>

          <ToastContainer position="bottom-right" />
        </ContentArea>
      </RootContainer>
    </ThemeProvider>
  );
}

export default BlogEditor;
